/**
 * Audio (§18) — Web Audio oscillator-tonen per mantra-kaart op de juiste Hz,
 * met een zachte fade-in/out-envelope (~1.2s). Bij de eindoverwinning (Theluun)
 * klinken alle 7 frequenties simultaan (~4s) als finale.
 *
 * Volledig procedureel (sinus-oscillator, geen samples). Alle calls zijn
 * no-ops als Web Audio niet beschikbaar is. Volume/mute worden persistent
 * bewaard via de veilige opslag.
 */
import { veiligeOpslag } from './store/veiligeOpslag';
import { ALLE_FREQUENTIES } from './data/headers';

const VOL_SLEUTEL = 'ca-audio-volume';
const DEMP_SLEUTEL = 'ca-audio-gedempt';

let ctx: AudioContext | null = null;
let masterVolume = leesVolume();
let gedempt = leesGedempt();

function leesVolume(): number {
  const r = veiligeOpslag.getItem(VOL_SLEUTEL);
  const v = r == null ? 0.6 : parseFloat(r);
  return Number.isFinite(v) ? Math.min(1, Math.max(0, v)) : 0.6;
}
function leesGedempt(): boolean {
  return veiligeOpslag.getItem(DEMP_SLEUTEL) === '1';
}

function audioContext(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  try {
    const AC = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AC) return null;
    if (!ctx) ctx = new AC();
    if (ctx.state === 'suspended') void ctx.resume();
    return ctx;
  } catch {
    return null;
  }
}

/** Speelt één sinus-toon met fade-envelope. `duur`/`piek` instelbaar (finale). */
function toon(hz: number, duur = 1.2, piekFactor = 0.3): void {
  if (gedempt || masterVolume <= 0) return;
  const ac = audioContext();
  if (!ac) return;
  const nu = ac.currentTime;
  const osc = ac.createOscillator();
  const gain = ac.createGain();
  osc.type = 'sine';
  osc.frequency.setValueAtTime(hz, nu);

  const piek = Math.max(0.0001, masterVolume * piekFactor);
  gain.gain.setValueAtTime(0.0001, nu);
  gain.gain.exponentialRampToValueAtTime(piek, nu + 0.08); // attack
  gain.gain.exponentialRampToValueAtTime(0.0001, nu + duur); // decay

  osc.connect(gain).connect(ac.destination);
  osc.start(nu);
  osc.stop(nu + duur + 0.05);
  osc.onended = () => { try { osc.disconnect(); gain.disconnect(); } catch { /* ignore */ } };
}

/** Speelt de mantra-tonen die de engine deze actie heeft uitgestoten. */
export function speelTonen(frequenties: number[]): void {
  if (!frequenties?.length) return;
  // Bij meerdere tegelijk iets zachter om clipping te voorkomen.
  const piek = frequenties.length > 1 ? 0.2 : 0.3;
  for (const hz of frequenties) toon(hz, 1.2, piek);
}

/** Eindfinale: alle 7 mantra-frequenties simultaan, langer aangehouden (§13/§18). */
export function speelFinale(): void {
  for (const hz of ALLE_FREQUENTIES) toon(hz, 4, 0.12);
}

/* — instellingen — */
export function getVolume(): number { return masterVolume; }
export function zetVolume(v: number): void {
  masterVolume = Math.min(1, Math.max(0, v));
  veiligeOpslag.setItem(VOL_SLEUTEL, String(masterVolume));
}
export function isGedempt(): boolean { return gedempt; }
export function zetGedempt(d: boolean): void {
  gedempt = d;
  veiligeOpslag.setItem(DEMP_SLEUTEL, d ? '1' : '0');
}
/** Korte test-toon (voor de volume-slider in instellingen). */
export function testToon(): void { toon(528, 0.8, 0.3); }
